import * as Actions from '../actions/userActions';
import User from '../../entities/user';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type UserAction = { 
    type: string; 
    user: User | null; 
    userId: string | User; 
    partial: boolean; 
    lastFetch: number; 
    users: User[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<User> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {User[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<User> | null, firmId: string, userId: string, updates?: User[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'user', 'users', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<User>} data
 * @param {User[]} [updates]
 * @returns {ArrayMap<User>}
 */
function updateStorage(data: ArrayMap<User>, updates?: User[]): ArrayMap<User> {
	if (logging) console.debug('⮂ userReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {User[]} [updates]
 * @returns {ArrayMap<User>}
 */
function updateStorageNoReturn(updates?: User[]): void {
	if (logging) console.debug('⮂ userReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {User[]} [data]
 * @returns {ArrayMap<User>}
 */
function createState(data?: User[] | ArrayMap<User>): ArrayMap<User> {
	if (!data) return ArrayMapRegistry.clone<User>('user', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<User>('user', data.map(e => e));
    else return ArrayMapRegistry.clone<User>('user', data);
}

function users(state: ArrayMap<User>, action: UserAction): ArrayMap<User> {
	if (!state) state = createState();

	let st: ArrayMap<User>;

	switch (action.type) {
		// action.user - the new value of user with id action.id
		// action.userId - id of the target, used if new value is null
		case Actions.USER_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ userReducer: FETCH_ONE_SUCCESS', action);

			if (action.user === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.userId)) {
				st = state.clone();
				st.set(action.user);
				// state.clear();

				updateStorageNoReturn([action.user]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.users
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.users
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.users
			 !partial, !stateEmpty, fetchEmpty	-> D return users in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge users in state newer than those in action.users
		 */
		case Actions.USER_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.users.length === 0;
			const fetched = action.users.map(user => new User({...user, persisted: true}));

			if (logging) console.debug(`⮂ userReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} users with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ userReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ userReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<User> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: User[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(user => {
					const current = st.get(user.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && user.status === 'D') {
							// Remove it from state
							toUpdate.push(user);
							st.delete(user);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (user.addedOn > current.addedOn) {
								toUpdate.push(user);
								st.set(user);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (user.status !== 'D') {
							toUpdate.push(user);
							st.set(user);
						}
						else if (logging) console.debug('⮂ userReducer: partialMerge: skipping deleted user not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ userReducer: empty: users', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ userReducer: fullMerge: users', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localUser => {
                    // If this user has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localUser.persisted) {
                        const fetchedUser = st.get(localUser.id);
                        if (!fetchedUser || localUser.addedOn > fetchedUser.addedOn) st.set(localUser);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.USER_FETCH_ONE_FAILURE:
			console.error('USER_FETCH_ONE_FAILURE', action.userId, action.err);

			// No update to storage required
			return state;

		case Actions.USER_FETCH_FAILURE:
			console.error('USER_FETCH_FAILURE', action.userId, action.err);

			// No update to storage required
			return state;

		case Actions.USER_RESTORE: {
		    let user = new User({...action.user});
            st = state.clone();

            const existing = st.get(user.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (user.addedOn >= existing.addedOn) {
                    st.set(user);
                    return updateStorage(st, [user]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (user.status === 'D') return state;

                st.set(user);
                return updateStorage(st, [user]);
            }
        }
            
		case Actions.USER_SAVE: {
		    // console.debug('⮂ USER_SAVE reducer', action.user?.id);
		    
			// clone the user
			let user = new User({...action.user});

			st = state.clone();
			// state.clear(); 
			
			
			// Clear computed fields
			user.lfm = null;
			user.lfmComputed = null;
			user.fml = null;
			user.fmlComputed = null;
			user.shorten = null;
			user.initials = null;

			// Add or update the new user in the state
			// If this is a create, add to the queue
			const _user = st.get(user.id);

			if (!_user) st.set(user);
			else {
				if (user.addedOn >= _user.addedOn) st.set(user);
			}

			return updateStorage(st, [user]);
		}

		case Actions.USER_SAVE_MANY: {
		    if (logging) console.debug('⮂ USER_SAVE_MANY reducer', action.users.map(user => user.id).join(','));
		    
			// clone the users
			let toProcess = action.users.map(user => new User(user));

			st = state.clone();
			// state.clear();

			const updates: User[] = [];

			toProcess.forEach(user => {
				// Add or update the new user in the state
				// If this is a create, add to the queue
				const existing = st.get(user.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (user.status !== 'D') {
						st.push(user);
						updates.push(user);
					}
					else if (logging) console.debug('⮂ USER_SAVE_MANY: skipping deleted user not in state');
				}
				else {
					if (user.addedOn >= existing.addedOn) {
						st.set(user);
						updates.push(user);
					}
					else if (logging) console.debug('⮂ Skipping, existing user had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.USER_REMOVE: {
			// clone the user
			const user = new User({...action.user});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(user);

			return updateStorage(st, [user]);
		}

		case Actions.USER_RESTORE_FAILURE:
		case Actions.USER_SAVE_FAILURE: {
		    console.warn('USER_SAVE_FAILURE reducer', action.user?.id);
		    if (!action.user?.id) {
				console.warn('USER_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const user = ensure(User, action.user);
			if (!user) {
				console.error('Could not find user after call to save');
				return state;
			}

			st = state.clone();

			const _userRef = state.get(user.id);

			if (_userRef && (_userRef.addedOn === user.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _user = new User({..._userRef});
				_user.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_user.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('user', _user).then(r => {
						if (r.isErr()) console.error(`⮂ userReducer: failed to save user with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ userReducer: saved user with fatalError info');
					}).catch(err => console.error(`⮂ userReducer: failed to save user with fatalError info: ${err.message}`));

				st.set(_user);
			}

			return updateStorage(st);
		}

		case Actions.USER_SAVE_MANY_FAILURE: {
		    console.warn('USER_SAVE_MANY_FAILURE reducer', action.users.map(user => user.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.USER_RESTORE_SUCCESS:
		case Actions.USER_SAVE_SUCCESS: {
			if (!action.user?.id) {
				console.warn('USER_SAVE_SUCCESS reducer');
                return state;
			}

			const userRef = state.get(action.user?.id);
			if (!userRef) {
				console.warn('Could not find user after call to save');
                return state;
			}

			st = state.clone();

			if (action.user.addedOn >= userRef.addedOn) {
				st.set(action.user);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const user = new User({...userRef, persisted: true});
				st.set(user);
			}

			return updateStorage(st, [action.user]);
		}

		case Actions.USER_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ USER_SAVE_MANY_SUCCESS reducer', action.users.map(user => user.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const users: User[] = [];

			action.users.forEach(e => {
				const userRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!userRef) console.warn('Could not find user after call to save');
				else if (e.addedOn >= userRef.addedOn) {
				    st.set(e);
				    users.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const user = new User({...userRef, persisted: true});
					st.set(user);
					users.push(user);
				}
			});

			return updateStorage(st, users);
		}

		case Actions.USER_REMOVE_FAILURE: {
		    console.warn('USER_REMOVE_FAILURE action', action.user?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.USER_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.USER_RESTORE_MANY: {
			if (logging) console.debug('⮂ USER_RESTORE_MANY reducer', action.users.map(user => user.id).join(','));

			const toProcess = action.users.map(user => new User({...user}));
			st = state.clone();

			const updates: User[] = [];

			toProcess.forEach(user => {
				const existing = st.get(user.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (user.addedOn >= existing.addedOn) {
						st.set(user);
						updates.push(user);
					}
					else if (logging) console.debug('⮂ USER_RESTORE_MANY: skipping older version of user');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (user.status !== 'D') {
						st.set(user);
						updates.push(user);
					}
					else if (logging) console.debug('⮂ USER_RESTORE_MANY: skipping deleted user not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.USER_REMOVE_MANY: {
			if (logging) console.debug('⮂ USER_REMOVE_MANY reducer', action.users.map(user => user.id).join(','));

			const toProcess = action.users.map(user => new User({...user}));
			st = state.clone();

			toProcess.forEach(user => {
				st.delete(user);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default users;